import { createTheme } from '@mui/material/styles';

/**
 * DynastyRep visual language.
 *
 * Design language: gritty, industrial, editorial. Deep near-black background
 * with a rich orange accent and a warm gold used sparingly for top ranks /
 * achievements. Barlow Condensed for uppercase headings and CTA buttons to
 * evoke sports-magazine aesthetics; DM Sans for body copy.
 */

// Design tokens (mirror the CSS custom properties in index.css so that raw
// HTML/CSS and MUI components look the same).
export const tokens = {
  bg: '#0a0a0c',
  surface: '#13131a',
  card: '#17171f',
  border: '#252530',
  border2: '#32323f',
  orange: '#e8722a',
  orange2: '#ff8c45',
  gold: '#c9a03e',
  // "Legendary" accent. Used sparingly — currently only the Commissioner
  // Score block, where we want a visually distinct, more elite-feeling
  // hue than the regular gold used for manager trust scores. A rich
  // royal violet that pops on the dark background and never collides
  // with the warm orange/gold brand palette.
  legendary: '#b465e8',
  legendaryGlow: '#d39bff',
  text: '#f0ece3',
  text2: '#9997a8',
  text3: '#5e5d6e',
} as const;

const fontHeading = '"Barlow Condensed", "Roboto Condensed", sans-serif';
const fontBody = '"DM Sans", "Roboto", system-ui, -apple-system, sans-serif';

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: tokens.orange,
      light: tokens.orange2,
      dark: '#c25e1c',
      contrastText: '#ffffff',
    },
    secondary: {
      main: tokens.gold,
      light: '#dab35c',
      dark: '#9b7a2f',
      contrastText: '#0a0a0c',
    },
    background: {
      default: tokens.bg,
      paper: tokens.card,
    },
    text: {
      primary: tokens.text,
      secondary: tokens.text2,
      disabled: tokens.text3,
    },
    success: {
      main: '#3bb87a',
      light: '#6ccd9a',
      dark: '#238f58',
    },
    warning: {
      main: '#f0b942',
      light: '#f5ce78',
      dark: '#b88a2b',
    },
    error: {
      main: '#e06b5a',
      light: '#ef907f',
      dark: '#b04c3e',
    },
    info: {
      main: '#7c6ff7',
      light: '#a99ff9',
      dark: '#5a4cd4',
    },
    divider: tokens.border,
  },
  typography: {
    fontFamily: fontBody,
    h1: {
      fontFamily: fontHeading,
      fontWeight: 900,
      letterSpacing: '-0.02em',
      textTransform: 'uppercase',
      lineHeight: 0.95,
    },
    h2: {
      fontFamily: fontHeading,
      fontWeight: 900,
      letterSpacing: '-0.01em',
      textTransform: 'uppercase',
      lineHeight: 0.95,
    },
    h3: {
      fontFamily: fontHeading,
      fontWeight: 800,
      textTransform: 'uppercase',
      letterSpacing: '-0.01em',
    },
    h4: {
      fontFamily: fontHeading,
      fontWeight: 800,
      textTransform: 'uppercase',
      letterSpacing: '0',
    },
    h5: {
      fontFamily: fontHeading,
      fontWeight: 700,
      letterSpacing: '0.3px',
    },
    h6: {
      fontFamily: fontHeading,
      fontWeight: 700,
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
    },
    button: {
      fontFamily: fontHeading,
      fontWeight: 700,
      letterSpacing: '1px',
      textTransform: 'uppercase',
    },
    body1: { fontFamily: fontBody, fontSize: '0.95rem', lineHeight: 1.55 },
    body2: { fontFamily: fontBody, fontSize: '0.875rem', lineHeight: 1.55 },
    caption: {
      fontFamily: fontBody,
      letterSpacing: '0.5px',
    },
    overline: {
      fontFamily: fontBody,
      fontWeight: 600,
      letterSpacing: '2.5px',
      textTransform: 'uppercase',
    },
  },
  shape: {
    // The design language uses squared-off edges. Keep a *tiny* radius so
    // MUI chips, snackbars etc. don't look overly harsh.
    borderRadius: 2,
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 0,
          padding: '10px 22px',
          fontSize: 14,
        },
        sizeLarge: {
          padding: '14px 32px',
          fontSize: 16,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        containedPrimary: {
          backgroundColor: tokens.orange,
          color: '#ffffff',
          '&:hover': { backgroundColor: tokens.orange2 },
        },
        containedSecondary: {
          backgroundColor: tokens.gold,
          color: tokens.bg,
          '&:hover': { backgroundColor: '#dab35c' },
        },
        outlined: {
          borderColor: 'rgba(255,255,255,0.18)',
          color: 'rgba(240,236,227,0.8)',
          '&:hover': {
            borderColor: 'rgba(255,255,255,0.4)',
            backgroundColor: 'rgba(255,255,255,0.03)',
          },
        },
        text: {
          color: tokens.text,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: tokens.card,
          border: `1px solid ${tokens.border}`,
          borderRadius: 2,
          transition: 'border-color 0.25s, transform 0.25s',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: 2,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: tokens.bg,
          borderBottom: `1px solid ${tokens.border}`,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255,255,255,0.02)',
            borderRadius: 2,
            '& fieldset': { borderColor: tokens.border },
            '&:hover fieldset': { borderColor: tokens.border2 },
            '&.Mui-focused fieldset': { borderColor: tokens.orange },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          fontFamily: fontHeading,
          fontWeight: 700,
          letterSpacing: '0.8px',
          textTransform: 'uppercase',
          fontSize: '0.7rem',
        },
      },
    },
    MuiRating: {
      styleOverrides: {
        iconFilled: { color: tokens.gold },
        iconHover: { color: '#dab35c' },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          border: `1px solid ${tokens.border}`,
        },
        standardInfo: {
          backgroundColor: 'rgba(124,111,247,0.1)',
          color: '#b3a9fa',
          '& .MuiAlert-icon': { color: '#a99ff9' },
        },
        standardSuccess: {
          backgroundColor: 'rgba(59,184,122,0.1)',
          color: '#7ed3a6',
          '& .MuiAlert-icon': { color: '#6ccd9a' },
        },
        standardWarning: {
          backgroundColor: 'rgba(240,185,66,0.1)',
          color: '#f5ce78',
          '& .MuiAlert-icon': { color: '#f0b942' },
        },
        standardError: {
          backgroundColor: 'rgba(224,107,90,0.1)',
          color: '#ef907f',
          '& .MuiAlert-icon': { color: '#e06b5a' },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: tokens.card,
          border: `1px solid ${tokens.border}`,
          borderRadius: 2,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: tokens.border },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: fontHeading,
          fontWeight: 700,
          letterSpacing: '1px',
          textTransform: 'uppercase',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: { fontFamily: fontBody },
        secondary: { fontFamily: fontBody },
      },
    },
  },
});
